import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Typography, Paper, Autocomplete, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Switch, CircularProgress
} from '@mui/material';
import { DepartmentService } from '../../services/department.service';
import { Department, DepartmentUser } from '../../interfaces/department.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { displayPersonName } from '../../utils/userUtils';
import { employeeLabelWithFullName, useEmployeeSearch, withSelected } from '../common/EmployeeAutocomplete';

interface InstitutionUser {
    id: string;
    username: string;
    fullName?: string;
    phone: string;
    type: string;
    isActive: boolean;
}

const DepartmentUserAssignment: React.FC = () => {
    const { departmentId } = useParams<{ departmentId: string }>();
    const navigate = useNavigate();
    const toast = useToast();

    const isInstitutionAdmin = usePermission(PERMISSIONS.DEPARTMENT_MANAGE);

    const [department, setDepartment] = useState<Department | null>(null);
    const [departmentUsers, setDepartmentUsers] = useState<DepartmentUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<InstitutionUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (departmentId) {
            fetchData();
        }
    }, [departmentId]);

    const {
        options: searchedUsers,
        loading: userSearchLoading,
        inputValue: userInput,
        handleInputChange: handleUserInputChange,
    } = useEmployeeSearch<InstitutionUser>({
        onError: (err) => toast.showApiError(err, 'Failed to search users'),
    });

    const fetchData = async () => {
        try {
            const [dept, deptUsers] = await Promise.all([
                DepartmentService.getDepartmentById(departmentId!),
                DepartmentService.getDepartmentUsers(departmentId!),
            ]);
            setDepartment(dept);
            setDepartmentUsers(deptUsers);

            const preSelected: InstitutionUser[] = deptUsers.map((du: DepartmentUser) => ({
                id: du.userId,
                username: du.username || du.userId,
                fullName: du.fullName ?? undefined,
                phone: '',
                type: '',
                isActive: true,
            }));
            setSelectedUsers(preSelected);
        } catch (err) {
            toast.showApiError(err, 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const userIds = selectedUsers.map(u => u.id);
            const updatedUsers = await DepartmentService.updateDepartmentUsers(departmentId!, userIds);
            setDepartmentUsers(updatedUsers);
            toast.showSuccess('Department users updated successfully');
        } catch (err) {
            toast.showApiError(err, 'Failed to update department users');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleAdmin = async (userId: string) => {
        try {
            const updated = await DepartmentService.toggleDepartmentAdmin(departmentId!, userId);
            setDepartmentUsers(prev =>
                prev.map(du => du.userId === userId ? { ...du, isDepartmentAdmin: updated.isDepartmentAdmin } : du)
            );
        } catch (err) {
            toast.showApiError(err, 'Failed to toggle admin status');
        }
    };

    const getUserName = (userId: string): string => {
        const found = departmentUsers.find(du => du.userId === userId);
        return found && found.username ? displayPersonName(found.fullName, found.username) : userId;
    };

    const autocompleteOptions = React.useMemo(
        () => withSelected(searchedUsers, selectedUsers),
        [selectedUsers, searchedUsers]
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ padding: 3 }} data-help-page="departments.members.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">
                    {department ? `${department.name} - Manage Users` : 'Manage Department Users'}
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/departments')} data-help="departments.members.back">
                    Back to Departments
                </Button>
            </Box>

            <Paper sx={{ padding: 3, marginBottom: 3 }} data-help-section="departments.members.assign">
                <Typography variant="h6" sx={{ marginBottom: 2 }}>Assign Users</Typography>
                <Autocomplete<InstitutionUser, true, false, false>
                    multiple
                    data-help="departments.members.select"
                    options={autocompleteOptions}
                    getOptionLabel={employeeLabelWithFullName}
                    value={selectedUsers}
                    onChange={(_, newValue) => setSelectedUsers(newValue)}
                    onInputChange={handleUserInputChange}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={userSearchLoading}
                    noOptionsText={userSearchLoading ? 'Loading…' : userInput ? 'No users found' : 'No users'}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Users" placeholder="Search users..." />
                    )}
                    sx={{ marginBottom: 2 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    data-help="departments.members.save"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Assignments'}
                </Button>
            </Paper>

            {departmentUsers.length > 0 && (
                <Paper sx={{ padding: 3 }} data-help-section="departments.members.table">
                    <Typography variant="h6" sx={{ marginBottom: 2 }}>Current Members</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="departments.members.col.employee">Employee</TableCell>
                                    {isInstitutionAdmin && <TableCell data-help="departments.members.col.admin">Department Admin</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {departmentUsers.map((du) => (
                                    <TableRow key={du.id}>
                                        <TableCell>{getUserName(du.userId)}</TableCell>
                                        {isInstitutionAdmin && (
                                            <TableCell>
                                                <Switch
                                                    checked={du.isDepartmentAdmin}
                                                    onChange={() => handleToggleAdmin(du.userId)}
                                                    data-help="departments.members.action.toggleAdmin"
                                                    color="primary"
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default DepartmentUserAssignment;
